import React, {useMemo, useCallback} from 'react';
import styles from './FundraisersTable.module.scss';
import {TableColumnConfig, Text, Spin, Button, Label} from '@gravity-ui/uikit';
import {formatDateToLocalDate, formatCurrency} from '../../shared';
import {FundraiserModel, getStatusLabel, BaseFundraiserTable} from '../../entities';
import {SortableHeader, SortProperty, SortOrder} from '../../features';
import {useInfiniteScroll} from '../../shared/hooks/useInfiniteScroll';
import {useNavigate} from 'react-router';

interface FundraisersTableProps {
  fundraisers: FundraiserModel[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  sortProperty?: SortProperty;
  sortOrder?: SortOrder;
  onSortChange: (property: SortProperty | undefined, order: SortOrder | undefined) => void;
}

export const FundraisersTable = ({
  fundraisers,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  sortProperty,
  sortOrder = 'ascending',
  onSortChange,
}: FundraisersTableProps) => {
  const handleSort = useCallback(
    (property: SortProperty) => {
      if (property === sortProperty) {
        const newOrder = sortOrder === 'ascending' ? 'descending' : 'ascending';
        onSortChange(property, newOrder);
      } else {
        onSortChange(property, 'ascending');
      }
    },
    [sortProperty, sortOrder, onSortChange],
  );

  const navigate = useNavigate();

  const handleRowClick = (fundraiser: FundraiserModel) => {
    navigate(`/fundraisings/appeals/${fundraiser.id}`);
  };

  const handleResetSort = useCallback(() => {
    onSortChange(undefined, undefined);
  }, [onSortChange]);

  const columns = useMemo<TableColumnConfig<FundraiserModel>[]>(
    () => [
      {
        id: 'name',
        name: () => <Text variant="subheader-1">Название сбора</Text>,
        template: (item: FundraiserModel) => (
          <div className={styles.fundraiserCellContainer}>
            <Text variant="body-1">{item.name}</Text>
            <div className={styles.labelsContainer}>
              {getStatusLabel(item.status)}
              {item.fund && (
                <Label size="xs" theme="normal" className={styles.label}>
                  {item.fund.name}
                </Label>
              )}

              {item.region && (
                <Label size="xs" theme="normal" className={styles.label}>
                  {item.region.name}
                </Label>
              )}

              {item.categories &&
                item.categories.length > 0 &&
                item.categories.map((category, index) => (
                  <Label key={index} size="xs" theme="normal" className={styles.label}>
                    {category.name}
                  </Label>
                ))}
            </div>
          </div>
        ),
      },
      {
        id: 'externalId',
        align: 'center',
        name: () => <Text variant="subheader-1">Номер сбора</Text>,
        template: (item: FundraiserModel) => (
          <div className={styles.fundraiserCellContainer}>
            <Text variant="body-1">{item.externalId || '—'}</Text>
          </div>
        ),
      },
      {
        id: 'amount',
        name: () => <Text variant="subheader-1">Сумма</Text>,
        template: (item: FundraiserModel) => (
          <div className={styles.fundraiserCellContainer}>
            <Text variant="body-1">{item.requiredAmount ? formatCurrency(item.requiredAmount) : 'Не указана'}</Text>
          </div>
        ),
      },
      {
        id: 'createdAt',
        name: () => (
          <SortableHeader
            title="Создан"
            property="createdAt"
            currentProperty={sortProperty}
            currentOrder={sortOrder}
            onSort={handleSort}
          />
        ),
        template: (item: FundraiserModel) => (
          <div className={styles.fundraiserCellContainer}>
            <Text variant="body-1">{formatDateToLocalDate(item.createdAt)}</Text>
          </div>
        ),
      },
      {
        id: 'amountCollectedAt',
        name: () => (
          <SortableHeader
            title="Собран"
            property="amountCollectedAt"
            currentProperty={sortProperty}
            currentOrder={sortOrder}
            onSort={handleSort}
          />
        ),
        template: (item: FundraiserModel) => (
          <div className={styles.fundraiserCellContainer}>
            <Text variant="body-1">
              {item.amountCollectedAt ? formatDateToLocalDate(item.amountCollectedAt) : 'Не собран'}
            </Text>
          </div>
        ),
      },
      {
        id: 'lastMediaUploadAt',
        name: () => (
          <SortableHeader
            title="Последняя загрузка"
            property="lastMediaUploadAt"
            currentProperty={sortProperty}
            currentOrder={sortOrder}
            onSort={handleSort}
          />
        ),
        template: (item: FundraiserModel) => (
          <div className={styles.fundraiserCellContainer}>
            <Text variant="body-1">{formatDateToLocalDate(item.lastMediaUploadAt) || '—'}</Text>
          </div>
        ),
      },
      {
        id: 'more',
        width: '15%',
        name: () => (
          <div className={styles.tableHeader}>
            <Button view="normal" size="m" onClick={handleResetSort} className={styles.resetSortButton}>
              <Text variant="body-2">Сбросить сортировку</Text>
            </Button>
          </div>
        ),
        template: () => ' ',
      },
    ],
    [handleSort, sortProperty, sortOrder, handleResetSort],
  );

  const {loadMoreRef} = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <div className={styles.tableContainer}>
      <BaseFundraiserTable
        fundraisers={fundraisers || []}
        isLoading={isLoading && !isFetchingNextPage}
        columns={columns}
        onRowClick={handleRowClick}
      />
      {isFetchingNextPage ? (
        <div className={styles.loadingSpinnerContainer}>
          <Spin size="m" />
        </div>
      ) : (
        hasNextPage && (
          <Button
            component={'div'}
            size="xl"
            width="max"
            view="normal"
            ref={loadMoreRef}
            className={styles.loadMoreTrigger}>
            <Text variant="body-2">Загрузить ещё</Text>
          </Button>
        )
      )}
    </div>
  );
};
